import React, {useEffect, useState} from 'react';
import {Alert, Card, Col, Input, Row, Select, Statistic, Table, Typography} from 'antd';
import api from '../utils/api';

const {Title} = Typography;

// Funnel order: each total is cumulative over the one after it, so the numbers
// read top-to-bottom as a pipeline.
const TOTALS = ['allocated', 'unregistered', 'registered', 'collected', 'received', 'processing', 'completed'];

const STATUS_OPTIONS = [
  {value: '', label: 'All statuses'},
  {value: 'issued', label: 'Issued'},
  {value: 'claimed', label: 'Registered'},
  {value: 'shipped', label: 'In the post'},
  {value: 'received', label: 'At the lab'},
  {value: 'processing', label: 'Being analysed'},
  {value: 'completed', label: 'Analysis complete'},
];

const PartnerKitsPage = () => {
  const [totals, setTotals] = useState({});
  const [kits, setKits] = useState([]);
  const [pagination, setPagination] = useState({current: 1, pageSize: 20, total: 0});
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async (page = 1, pageSize = 20, nextStatus = status, q = query) => {
    setLoading(true); setError(null);
    try {
      const params = {page, per_page: pageSize};
      if (nextStatus) params.status = nextStatus;
      if (q) params.q = q;
      const [summaryResult, kitsResult] = await Promise.all([
        api.get('/v1/partner/kits/summary'),
        api.get('/v1/partner/kits', {params}),
      ]);
      setTotals(summaryResult.data.totals || {});
      setKits(kitsResult.data.kits || []);
      const meta = kitsResult.data.metadata || {};
      setPagination({current: meta.page || page, pageSize: meta.per_page || pageSize, total: meta.total || 0});
    } catch (requestError) {
      setKits([]);
      setError(requestError.response?.status === 403
        ? 'Kit access is unavailable or has been revoked.'
        : 'Could not load kit inventory.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return <div className="cardContainer" style={{margin: 24, padding: 24}}>
    <Title level={2}>Kit inventory</Title>
    {error && <Alert type="error" showIcon message={error} style={{marginBottom: 16}}/>}
    <Row gutter={[16, 16]}>{TOTALS.map(key =>
      <Col xs={12} md={6} lg={3} key={key}>
        <Card><Statistic title={key.replaceAll('_', ' ')} value={totals[key] || 0}/></Card>
      </Col>)}</Row>
    <Card
      title="Kits allocated to you"
      style={{marginTop: 16}}
      extra={<Input.Search
        value={query}
        onChange={e => setQuery(e.target.value)}
        onSearch={value => load(1, pagination.pageSize, status, value)}
        placeholder="Search barcode"
        style={{width: 220}}/>}
    >
      <Select
        value={status}
        options={STATUS_OPTIONS}
        onChange={value => { setStatus(value); load(1, pagination.pageSize, value, query); }}
        style={{width: 200, marginBottom: 16}}/>
      <Table
        loading={loading}
        dataSource={kits}
        rowKey="barcode"
        pagination={{...pagination, showSizeChanger: false}}
        onChange={page => load(page.current, page.pageSize, status, query)}
        columns={[
          {title: 'Barcode', dataIndex: 'barcode'},
          {title: 'Status', dataIndex: 'status'},
          {title: 'Registered', render: (_, kit) => (kit.is_registered ? 'Yes' : 'No')},
          // Name only appears when this partner holds an active care-access
          // grant for that patient. A blank here is deliberate, not a bug:
          // a kit can be registered by someone this partner never referred.
          {title: 'Customer', render: (_, kit) => (kit.patient ? `${kit.patient.first_name} ${kit.patient.last_name}` : '—')},
          {title: 'Allocated', dataIndex: 'assigned_at'},
          {title: 'Posted', render: (_, kit) => kit.shipped_at || '—'},
          {title: 'At lab', render: (_, kit) => kit.received_at || '—'},
        ]}/>
    </Card>
  </div>;
};

export default PartnerKitsPage;
